<template>
   <article class="legal-doc">
      <h1>Cookie Policy di SpotFish</h1>
      <p class="legal-updated">Ultimo aggiornamento: 17 luglio 2024 — Versione 3</p>

      <p>
         Questo documento contiene informazioni sulle tecnologie che consentono a SpotFish di
         raggiungere le finalità descritte di seguito. Tali tecnologie permettono al Titolare di
         raccogliere e salvare informazioni (ad esempio tramite l'uso di Cookie) o di utilizzare
         risorse (ad esempio eseguendo script) sul dispositivo dell'Utente durante l'interazione con
         SpotFish.
      </p>
      <p>
         Per semplicità, queste tecnologie sono definite "Strumenti di Tracciamento" salvo che sia
         necessaria una differenziazione. Sebbene i Cookie possano essere utilizzati nei browser web
         e mobili, questi non sarebbero applicabili alle applicazioni per dispositivi mobili poiché
         i Cookie richiedono la presenza del browser. Pertanto, il termine "Cookie" qui si riferisce
         specificamente a quel particolare tipo di Strumento di Tracciamento.
      </p>
      <p>
         Alcune delle finalità per le quali vengono impiegati Strumenti di Tracciamento potrebbero
         richiedere il consenso dell'Utente. Quando il consenso è prestato, può essere liberamente
         revocato in qualsiasi momento seguendo le istruzioni in questo documento.
      </p>
      <p>
         SpotFish utilizza Strumenti di Tracciamento gestiti direttamente dal Titolare (prima parte)
         e Strumenti di Tracciamento che abilitano servizi di terze parti (terza parte). Salvo
         diversa indicazione, queste terze parti accedono ai propri rispettivi Strumenti di
         Tracciamento. La durata di Cookie e strumenti simili varia secondo le impostazioni del
         Titolare o del fornitore terzo. Alcuni scadono dopo la sessione di navigazione dell'Utente.
      </p>
      <p>
         Oltre alle descrizioni delle categorie di seguito, gli Utenti possono ottenere informazioni
         dettagliate sulla durata e altre informazioni rilevanti nelle privacy policy delle terze
         parti tramite i link forniti o contattando il Titolare.
      </p>

      <h2>Come SpotFish utilizza gli Strumenti di Tracciamento</h2>

      <h3>Necessari</h3>
      <p>
         SpotFish utilizza Cookie "tecnici" e Strumenti di Tracciamento simili per attività
         strettamente necessarie a garantire il funzionamento o l'erogazione del Servizio.
      </p>

      <h4>Strumenti di Tracciamento gestiti da terze parti</h4>
      <h4>Cloudflare Bot Management — Cloudflare, Inc.</h4>
      <p>
         Cloudflare Bot Management fornisce un servizio di protezione da bot e gestione dei bot
         malevoli da parte di Cloudflare, Inc.
      </p>
      <p><strong>Dati Personali trattati:</strong></p>
      <p>
         Apertura dell'applicazione, area metropolitana, CAP, città, click, contea, cronologia di
         ricerca, cronologia di navigazione, dati di utilizzo, dati di visualizzazione video, durata
         della sessione, eventi di interazione, eventi di pagina, eventi di pressione tasti, eventi
         personalizzati, eventi del sensore di movimento, eventi touch, informazioni sul browser,
         informazioni sul dispositivo, informazioni sull'app, interazioni di scorrimento della
         pagina, indirizzo IP, avvii dell'app, latitudine della città, lingua, log del dispositivo,
         longitudine della città, movimenti del mouse, nazione, numero di sessioni, visualizzazioni
         di pagina, posizione di scorrimento, provincia, regione geografica, sistemi operativi,
         statistiche di sessione, stato e Strumenti di Tracciamento.
      </p>
      <p><strong>Luogo del trattamento:</strong> Stati Uniti</p>

      <p><strong>Durata degli Strumenti di Tracciamento:</strong></p>
      <ul>
         <li><code>__cf_bm</code>: 3 secondi</li>
         <li><code>__cfruid</code>: durata della sessione</li>
         <li><code>_cfuvid</code>: indefinita</li>
         <li><code>cf_clearance</code>: 30 minuti</li>
         <li><code>cf_ob_info</code>: 3 secondi</li>
         <li><code>cf_use_ob</code>: 3 secondi</li>
         <li><code>cfmrk_cic</code>: 3 mesi</li>
      </ul>

      <h3>Esperienza</h3>
      <p>
         SpotFish utilizza Strumenti di Tracciamento per migliorare la qualità dell'esperienza
         utente e consentire interazioni con contenuti, reti e piattaforme esterne.
      </p>

      <h4>Strumenti di Tracciamento gestiti da terze parti</h4>
      <h4>Google Fonts — Google LLC</h4>
      <p>
         Google Fonts è un servizio di visualizzazione di stili di font fornito da Google LLC che
         consente a SpotFish di integrare tali contenuti nelle proprie pagine.
      </p>
      <ul>
         <li>
            <strong>Dati Personali trattati:</strong> Dati di utilizzo, Strumenti di Tracciamento
         </li>
         <li><strong>Luogo del trattamento:</strong> Stati Uniti</li>
      </ul>

      <h2>Gestione delle preferenze e consenso</h2>
      <p>
         Quando l'uso degli Strumenti di Tracciamento è basato sul consenso, gli Utenti possono
         prestare o revocare il consenso impostando o aggiornando le proprie preferenze tramite il
         pannello delle scelte sulla privacy disponibile su SpotFish.
      </p>
      <p>
         Per quanto riguarda gli Strumenti di Tracciamento di terze parti, gli Utenti possono
         gestire le proprie preferenze visitando i link di opt-out pertinenti (se disponibili),
         utilizzando gli strumenti descritti nelle privacy policy delle terze parti o contattando
         direttamente le terze parti.
      </p>

      <h3>Controllare o eliminare i Cookie tramite le impostazioni del dispositivo</h3>
      <p>Gli Utenti possono utilizzare le impostazioni del browser per:</p>
      <ul>
         <li>Visualizzare quali Cookie o tecnologie simili sono impostati sui dispositivi</li>
         <li>Bloccare Cookie o tecnologie simili</li>
         <li>Eliminare Cookie o tecnologie simili dai browser</li>
      </ul>
      <p>
         Le impostazioni del browser non consentono il controllo granulare del consenso per
         categoria.
      </p>
      <p>
         Gli Utenti possono trovare informazioni sulla gestione dei Cookie per i browser più diffusi
         ai seguenti indirizzi:
      </p>
      <ul>
         <li>Google Chrome</li>
         <li>Mozilla Firefox</li>
         <li>Apple Safari</li>
         <li>Microsoft Internet Explorer</li>
         <li>Microsoft Edge</li>
         <li>Brave</li>
         <li>Opera</li>
      </ul>
      <p>
         Gli Utenti possono inoltre gestire alcuni Strumenti di Tracciamento delle applicazioni
         mobili disabilitandoli tramite le impostazioni del dispositivo come le impostazioni di
         pubblicità mobile o le impostazioni generali di tracciamento.
      </p>

      <h3>Conseguenze del rifiuto degli Strumenti di Tracciamento</h3>
      <p>
         Gli Utenti sono liberi di decidere se consentire o meno l'uso degli Strumenti di
         Tracciamento. Tuttavia, questi strumenti permettono a SpotFish di fornire un'esperienza
         migliore e funzionalità avanzate (in linea con le finalità qui descritte). Se gli Utenti
         bloccano l'uso degli Strumenti di Tracciamento, il Titolare potrebbe non essere in grado di
         fornire le funzionalità correlate.
      </p>

      <h2>Titolare del Trattamento dei Dati</h2>
      <p>
         Filtex 1965 Distribution SAS<br />
         Via Mario Colletta 15, 13811 Andorno Micca (BI), Italia<br />
         Email: <a href="mailto:info@spotfish.app">info@spotfish.app</a>
      </p>
      <p>
         Poiché l'uso di Strumenti di Tracciamento di terze parti su SpotFish non può essere
         completamente controllato dal Titolare, i riferimenti specifici agli Strumenti di
         Tracciamento di terze parti sono indicativi. Per informazioni complete, gli Utenti sono
         invitati a consultare le rispettive privacy policy dei servizi di terze parti elencati qui.
      </p>
      <p>
         Data la complessità di identificazione delle tecnologie di tracciamento, gli Utenti sono
         invitati a contattare il Titolare per ulteriori informazioni sull'uso delle tecnologie su
         SpotFish.
      </p>

      <h2>Definizioni e riferimenti legali</h2>
      <dl>
         <dt>Dato Personale (o Dato)</dt>
         <dd>
            Qualsiasi informazione che, direttamente o indirettamente, anche in collegamento con
            altre informazioni, inclusi numeri di identificazione personale, renda identificata o
            identificabile una persona fisica.
         </dd>
         <dt>Dati di Utilizzo</dt>
         <dd>
            Informazioni raccolte automaticamente attraverso SpotFish (incluse applicazioni di terze
            parti integrate), tra cui: indirizzi IP o nomi di dominio dei computer che si collegano
            a SpotFish, indirizzi URI, orario della richiesta, metodo utilizzato per la richiesta al
            server, dimensione del file ottenuto, codice numerico indicante lo stato della risposta
            del server, paese di origine, caratteristiche del browser e del sistema operativo, le
            varie connotazioni temporali della visita e dettagli sull'itinerario seguito all'interno
            dell'applicazione.
         </dd>
         <dt>Utente</dt>
         <dd>
            L'individuo che utilizza SpotFish e che, salvo diversa indicazione, coincide con
            l'Interessato.
         </dd>
         <dt>Interessato</dt>
         <dd>La persona fisica a cui si riferiscono i Dati Personali.</dd>
         <dt>Responsabile del Trattamento (o Responsabile)</dt>
         <dd>
            La persona fisica, giuridica, pubblica amministrazione o qualsiasi altro ente che tratta
            i Dati Personali per conto del Titolare, come descritto in questa privacy policy.
         </dd>
         <dt>Titolare del Trattamento (o Titolare)</dt>
         <dd>
            La persona fisica o giuridica, pubblica autorità, servizio o altro ente che, da solo o
            congiuntamente ad altri, determina le finalità e i mezzi del trattamento dei Dati
            Personali e gli strumenti adottati, incluse le misure di sicurezza per il funzionamento
            e l'utilizzo di SpotFish.
         </dd>
         <dt>SpotFish (o questa Applicazione)</dt>
         <dd>
            Lo strumento hardware o software attraverso il quale i Dati Personali degli Utenti
            vengono raccolti e trattati.
         </dd>
         <dt>Servizio</dt>
         <dd>
            Il Servizio fornito da SpotFish come definito nei relativi termini su questo
            sito/applicazione.
         </dd>
         <dt>Unione Europea (o UE)</dt>
         <dd>
            Salvo diversa indicazione, ogni riferimento all'Unione Europea si estende a tutti gli
            attuali stati membri dell'Unione Europea e dello Spazio Economico Europeo.
         </dd>
         <dt>Cookie</dt>
         <dd>
            I Cookie sono Strumenti di Tracciamento costituiti da piccole porzioni di dati
            conservati nel browser dell'Utente.
         </dd>
         <dt>Strumento di Tracciamento</dt>
         <dd>
            Qualsiasi tecnologia — come Cookie, identificatori univoci, web beacon, script
            incorporati, e-tag e fingerprinting — che consente il tracciamento dell'Utente, ad
            esempio raccogliendo o salvando informazioni sul dispositivo.
         </dd>
      </dl>

      <p class="legal-footer">
         Salvo diversa indicazione, questa policy riguarda esclusivamente SpotFish.
      </p>
   </article>
</template>

<style scoped>
@reference '../../assets/css/main.css';

.legal-doc {
   @apply p-6 sm:p-10 md:p-14 lg:p-20;
}

.legal-doc h1 {
   @apply h-xxl mb-6;
}

.legal-doc h2 {
   @apply h-xl mb-4 mt-10;
}

.legal-doc h3 {
   @apply h-l mb-3 mt-6;
}

.legal-doc h4 {
   @apply h-m mb-2 mt-4;
}

.legal-doc p {
   @apply p-m text-grey mb-3;
}

.legal-doc ul {
   @apply p-m text-grey mb-4 list-disc pl-6;
}

.legal-doc li {
   @apply mb-1;
}

.legal-doc dl {
   @apply p-m text-grey mt-4;
}

.legal-doc dt {
   @apply mt-4 font-bold text-black;
}

.legal-doc dd {
   @apply mb-2 pl-4;
}

.legal-doc a {
   @apply text-primary underline;
}

.legal-doc code {
   @apply bg-light-grey rounded-xsmall px-1.5 py-0.5 text-[0.8rem];
}

.legal-updated {
   @apply p-s text-grey mb-6;
}

.legal-footer {
   @apply p-s text-grey border-light-grey mt-10 border-t pt-6;
}
</style>
